import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { AzelRange, LOADING_CIRCLE, azElErrorMessage } from 'common/utils/constants';
import {
  generateHeatmapColorFromVal,
  isAzValid,
  isElValid,
  azelToHeatmapIndex,
  getDefaultHeatmapRange
} from 'common/utils/helpers';
import BeamRankDisplay from 'components/features/BeamRankDisplay/BeamRankDisplay';
import RasterHeatmapSlider from './RasterHeatmapSlider/RasterHeatmapSlider';
import './RasterHeatmap.css';
import { GA2_ACQ_KPI_NAMES, GA2_ACQ_PCI_NAMES, getGa2PciValue } from './utils';

class RasterHeatmap extends PureComponent {
  static defaultProps = {
    config: AzelRange,
    range: getDefaultHeatmapRange('ga1')
  };

  constructor(props) {
    super(props);
    this.crossCanvasRef = React.createRef();
    this.state = {
      range: this.props.range,
      height: null,
      width: null,
      heatmapDrawn: false,
      beamSelectDrawn: false,
      location: null,
      selectedBeamRank: 1
    };
  }

  componentDidMount() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  componentDidUpdate(prevProps, prevState) {
    this.drawCrossCanvas();
    if (!this.state.heatmapDrawn && this.props.heatmap.values != null && this.heatmapCanvasRef) {
      this.drawHeatmap();
      this.drawBeamSelect();
    }

    if (
      prevProps.selectedBeamRankIndex !== this.props.selectedBeamRankIndex ||
      prevProps.customAzEl?.az !== this.props.customAzEl?.az ||
      prevProps.customAzEl?.el !== this.props.customAzEl?.el
    ) {
      this.drawBeamSelect();
      this.props.setAzElInputError && this.props.setAzElInputError({ az: null, el: null });
    }

    if (
      this.state.heatmapDrawn &&
      (this.state.range.min !== prevState.range.min || this.state.range.max !== prevState.range.max)
    ) {
      this.drawHeatmap();
    }
  }

  onResize = () => {
    const { config } = this.props;
    let width = Math.min(this.heatmapDivRef.clientWidth, this.heatmapDivRef.clientWidth); //width will most likely be limiting factor on small screens
    let height = Math.round(width / config.ratio);
    this.setState({ height: height, width: width, heatmapDrawn: false, beamSelectDrawn: false });
  };

  drawBeamSelect() {
    const { config } = this.props;
    if (this.num_steps_x == null) {
      this.calculateSizes();
    }
    let targets = [];
    let arcs = [];
    let cross = [];

    let beamRankTable = this.props.beamrank;

    for (let i = 0; i < beamRankTable.length; i++) {
      /* do same calculation as we do when we build the heatmap array, to match azel in beamrank table to index in heatmap array*/
      let result = azelToHeatmapIndex(beamRankTable[i].az, beamRankTable[i].el, config);
      targets.push(result);
    }

    if (
      this.props.customAzEl.az !== null &&
      this.props.customAzEl.az !== '' &&
      this.props.customAzEl.el !== null &&
      this.props.customAzEl.el !== ''
    ) {
      targets.push(azelToHeatmapIndex(this.props.customAzEl.az, this.props.customAzEl.el, config));
    }

    let active = azelToHeatmapIndex(0, 0, config);
    let width = this.state.width;
    let height = this.state.height;
    let ctx = this.beamSelectCanvasRef.getContext('2d');
    ctx.clearRect(0, 0, width, height);

    let num_steps_x = config.az.num;
    let num_steps_y = config.el.num;
    let box_width = width / num_steps_x;
    let box_height = height / num_steps_y;

    for (let i = 0; i < targets.length; i++) {
      let x = Math.round(width - box_width * (targets[i][1] + 1));
      let y = Math.round(height - (targets[i][0] + 1) * box_height);
      // this.drawRoundedRectangle(ctx, x, y, 65, 25, 5, targets[i][1], targets[i][0]);

      arcs.push([x + box_width / 2, y + box_height / 2, Math.max(box_width, box_height) / 2]);
    }

    let x = Math.round(width - box_width * (active[1] + 1));
    let y = Math.round(height - (active[0] + 1) * box_height);
    let overshoot = Math.max(box_width, box_height) / 2;

    cross = [
      x - overshoot,
      y + box_height / 2,
      x + box_width + overshoot,
      y + box_height / 2,
      x + box_width / 2,
      y - overshoot,
      x + box_width / 2,
      y + box_height + overshoot
    ];
    // this draws the cross at the center of the heatmap
    if (cross.length === 8) {
      //if all arguments of the cross have been supplied
      ctx.beginPath();
      ctx.moveTo(cross[0] - 5, cross[1] - 7);
      ctx.lineTo(cross[2], cross[3] - 7);
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'black';
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cross[4] - 3, cross[5] - 7);
      ctx.lineTo(cross[6] - 3, cross[7] - 7);
      ctx.stroke();
    }

    for (let i = 0; i < arcs.length; i++) {
      let circleX = arcs[i][0] - 3;
      let circleY = arcs[i][1] - 6;
      let radius = arcs[i][2];
      ctx.beginPath();
      ctx.arc(circleX, circleY, radius, 0, 2 * Math.PI);
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#FFFFFF';
      if (this.props.selectedBeamRankIndex === i + 1) {
        // draw circle hairs
        ctx.moveTo(circleX, circleY + 8);
        ctx.lineTo(circleX, circleY + 12);

        ctx.moveTo(circleX, circleY - 8);
        ctx.lineTo(circleX, circleY - 12);

        ctx.moveTo(circleX + 8, circleY);
        ctx.lineTo(circleX + 12, circleY);

        ctx.moveTo(circleX - 8, circleY);
        ctx.lineTo(circleX - 12, circleY);
      }
      ctx.stroke();

      ctx.fillStyle = '#FFFFFF';

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = 'bold 10px Verdana';

      ctx.fillText(i === 3 ? 'C' : `${i + 1}`, (arcs[i][0] + arcs[i][0]) / 2 - 3, (arcs[i][1] + arcs[i][1]) / 2 - 6);
    }

    this.setState({ beamSelectDrawn: true });
  }

  drawHeatmap() {
    const { config } = this.props;
    if (this.num_steps_x == null) {
      this.calculateSizes();
    }
    let { width, height, range } = this.state;

    let ctx = this.heatmapCanvasRef.getContext('2d');
    ctx.clearRect(0, 0, width, height);

    let num_steps_x = config.az.num;
    let num_steps_y = config.el.num;
    let box_width = width / num_steps_x;
    let box_height = height / num_steps_y;

    let val = 0;
    let color;

    let x = 0;
    let y = height - box_height;

    for (let i = 0; i < num_steps_y; i++) {
      x = width - box_width;
      for (let j = 0; j < num_steps_x; j++) {
        val = this.props.heatmap.values[i][j];
        color = generateHeatmapColorFromVal(val, range.max, range.min);
        ctx.fillStyle = 'rgb(' + color.red + ', ' + color.green + ', ' + color.blue + ')';

        // draw each grid background color
        ctx.fillRect(Math.round(x), Math.round(y), Math.ceil(box_width), Math.ceil(box_height));
        //draw grid border line
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 0.2;
        ctx.strokeRect(x, y, box_width, box_height);

        x -= box_width;
      }
      y -= box_height;
    }

    this.setState({ heatmapDrawn: true });
  }

  //draw the rounded-edge rectangles such as [-1, 10]
  drawRoundedRectangle(ctx, x, y, w, h, r, az, el) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.beginPath();
    ctx.moveTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y - r + h);
    ctx.lineTo(x, y + r);
    ctx.fill();

    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '15px Arial';
    ctx.fillText(
      '' + -az.toFixed(0).toString() + ', ' + el.toFixed(0).toString(),
      (x + x + w) / 2,
      (y + y + h) / 2 + 1
    );
  }

  drawCrossCanvas() {
    const { config } = this.props;
    if (this.state.location == null) {
      return;
    }

    let ctx = this.crossCanvasRef.current.getContext('2d');

    ctx.clearRect(0, 0, this.state.width, this.state.height);
    ctx.strokeStyle = 'black';
    ctx.beginPath();
    ctx.moveTo(0, this.state.location.y);
    ctx.lineTo(this.calcWidth, this.state.location.y);
    ctx.stroke();
    ctx.stroke(); //double stroke, had trouble with slight transparency otherwise

    ctx.beginPath();
    ctx.moveTo(this.state.location.x, 0);
    ctx.lineTo(this.state.location.x, this.calcHeight);
    ctx.setLineDash([5, 5]);
    ctx.stroke();
    ctx.stroke();

    let az = config.az.min + (this.state.location.x / this.box_width) * config.az.stepSize;
    let el = config.el.max - (this.state.location.y / this.box_height) * config.el.stepSize;

    az = Math.round(az);
    el = Math.round(el);

    let x, y;
    if (az >= 0) {
      x = this.state.location.x - 70;
    } else {
      x = this.state.location.x + 5;
    }
    if (el <= 0) {
      y = this.state.location.y - 30;
    } else {
      y = this.state.location.y + 5;
    }
    this.drawRoundedRectangle(ctx, x, y, 65, 25, 5, az, el);
  }

  calculateSizes() {
    const { config } = this.props;

    this.num_steps_x = config.az.num;
    this.num_steps_y = config.el.num;

    this.box_width = this.state.width / this.num_steps_x;
    this.box_height = this.state.height / this.num_steps_y;

    this.calcHeight = this.num_steps_y * this.box_height;
    this.calcWidth = this.num_steps_x * this.box_width;
  }

  handleRangeChange = (range) => {
    this.setState({ range });
  };

  getPciAcqTitle = () => {
    const index = this.props.selectedBeamRankIndex;
    if (index === 0) {
      return 'Please select a rank from above';
    }
    if (index === 4) {
      return 'Acquisition PCI Values for Custom Rank';
    }

    return `Acquisition PCI Values for Rank ${index}`;
  };

  render() {
    const { className, config, heatmap, setAzElInputError, setCustomAzEl, setSelectedBeamRankIndex } = this.props;

    return (
      <div className={classNames('RasterHeatmap', className)}>
        <div className="RasterHeatmap_Heatmap">
          <div
            data-testid="wrapper"
            className="RasterHeatmap_Heatmap_Div"
            ref={(ref) => (this.heatmapDivRef = ref)}
            style={{ height: this.state.height != null ? this.state.height + 'px' : 'auto' }}
          >
            {this.state.width && heatmap?.values ? (
              <div className="RasterHeatmap_Heatmap_Inner_Div">
                <canvas
                  data-testid="heatmap"
                  ref={(ref) => (this.heatmapCanvasRef = ref)}
                  height={this.state.height}
                  width={this.state.width}
                />
                <canvas
                  data-testid="beams"
                  ref={(ref) => (this.beamSelectCanvasRef = ref)}
                  height={this.state.height}
                  width={this.state.width}
                />
                <canvas
                  data-testid="cross"
                  height={this.state.height}
                  width={this.state.width}
                  ref={this.crossCanvasRef}
                  onMouseMove={(event) => {
                    let x = event.nativeEvent.offsetX;
                    let y = event.nativeEvent.offsetY;
                    if (
                      this.state.location == null ||
                      Math.abs(this.state.location.x - x) > 2 ||
                      Math.abs(this.state.location.y - y) > 2
                    ) {
                      if (x < this.calcWidth && y < this.calcHeight) {
                        this.setState({ location: { x: x, y: y } });
                      }
                    }
                  }}
                  onClick={(event) => {
                    let az =
                      this.state.location &&
                      config.az.min + (this.state.location.x / this.box_width) * config.az.stepSize;
                    let el =
                      this.state.location &&
                      config.el.max - (this.state.location.y / this.box_height) * config.el.stepSize;

                    // below will capture Az and El as displayed on heatmap
                    az = Math.round(az);
                    el = Math.round(el);

                    setAzElInputError({
                      az: !isAzValid(az, config) ? azElErrorMessage.az(config) : null,
                      el: !isElValid(el, config) ? azElErrorMessage.el(config) : null
                    });
                    setCustomAzEl({ az: az * -1, el });
                    setSelectedBeamRankIndex(4);
                  }}
                />
              </div>
            ) : (
              LOADING_CIRCLE
            )}
          </div>
          <div>
            <RasterHeatmapSlider
              min={this.props.heatmap.min}
              max={this.props.heatmap.max}
              value={this.state.range}
              heatmap={this.props.heatmap}
              onChange={this.handleRangeChange}
            />
          </div>
        </div>
        {this.props.isGa2 ? (
          <div className="RasterHeatmap_BeamRank_Ga2">
            <div className="RasterHeatmap_BeamRank_Ga2_RankTable">
              <div className="RasterHeatmap_BeamRank_Ga2_PCI_Info_Top">
                {GA2_ACQ_KPI_NAMES.map((name) => (
                  <div className="RasterHeatmap_BeamRank_Ga2_AcqTable_SinglePci">
                    <div className="RasterHeatmap_BeamRank_Ga2_AcqTable_Name">{name}</div>
                    <div className="RasterHeatmap_BeamRank_Ga2_AcqTable_Value">
                      {getGa2PciValue(this.props.beamrank, name, this.props.acqData, this.props.selectedBeamRankIndex, this.props.customAzEl)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="RasterHeatmap_BeamRank_Table_Container">
                <div className="RasterHeatmap_BeamRank_Table_InnerContainer">
                  {this.props.beamrank ? <BeamRankDisplay {...this.props} parent={this} /> : LOADING_CIRCLE}
                </div>
              </div>
            </div>
            <div className="RasterHeatmap_BeamRank_Ga2_AcqTable">
              <div className="RasterHeatmap_BeamRank_Ga2_AcqTable_Title">{this.getPciAcqTitle()}</div>
              <div className="RasterHeatmap_BeamRank_Ga2_AcqTable_Data">
                {GA2_ACQ_PCI_NAMES.map((name) => (
                  <div className="RasterHeatmap_BeamRank_Ga2_AcqTable_SinglePci">
                    <div className="RasterHeatmap_BeamRank_Ga2_AcqTable_Name">{name}</div>
                    <div className="RasterHeatmap_BeamRank_Ga2_AcqTable_Value">
                      {getGa2PciValue(this.props.beamrank, name, this.props.acqData, this.props.selectedBeamRankIndex,this.props.customAzEl)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="RasterHeatmap_BeamRank">
            <div className="RasterHeatmap_BeamRank_Table_Container">
              <div className="RasterHeatmap_BeamRank_Table_InnerContainer">
                {this.props.beamrank ? <BeamRankDisplay {...this.props} parent={this} /> : LOADING_CIRCLE}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default connect((store, ownProps) => {
  const { heatmap, beamrank, azimuth, elevation } = ownProps;

  return {
    heatmap: heatmap ?? store.data.heatmap,
    beamrank: beamrank ?? store.data.heatmap.beamrank,
    azimuth: azimuth ?? store.data.heatmap?.activeBeam?.azimuth,
    elevation: elevation ?? store.data.heatmap?.activeBeam?.elevation
  };
})(RasterHeatmap);
